import React, { useState } from "react";
import { Formik, Form } from "formik";
import FormField from "components/FormField";
import Button from "components/Button";
import { apiFetch, formatUiErrors, FORM_MODES } from "utils";
import { toast } from "react-toastify";
import { useAuthState, useAuthDispatch } from "context";
import { useMutation, useQuery, useQueryClient } from "react-query";
import Error from "components/Error";
import { useUserState } from "context";

export default function LocationMapping() {
  const { token } = useAuthState();
  const dispatch = useAuthDispatch();
  const queryClient = useQueryClient();
  const { organization } = useUserState();
  const orgId = organization?.id;
  const [mappingData, setMappingData] = useState([]);
  const [formMode, setFormMode] = useState(FORM_MODES.VIEW);

  const {
    isFetching,
    error,
    data: queryData,
  } = useQuery(
    ["campusMappings", { orgId }],
    () =>
      apiFetch({
        token,
        dispatch,
        endpoint: `/api/v1/shelterluv_campus_mappings?organization_id=${orgId}`,
      }),
    {
      onSuccess: (data) => {
        setMappingData(data.data?.mappings ?? []);
      },
    }
  );

  const resolvedMappingData =
    mappingData.length > 0 ? mappingData : queryData?.data?.mappings ?? [];

  const locations = queryData?.data?.locations ?? [];

  const handleSave = (updatedRow) => {
    const index = resolvedMappingData.findIndex(
      (row) => row.campus === updatedRow.campus
    );
    const newData = [...resolvedMappingData];
    newData[index] = updatedRow;
    setMappingData(newData);
  };

  const { mutate: saveMappings, isLoading } = useMutation(
    () =>
      apiFetch({
        token,
        dispatch,
        method: "POST",
        endpoint: "/api/v1/shelterluv_campus_mappings",
        body: {
          organization_id: orgId,
          mappings: resolvedMappingData
            .filter((row) => row.campus !== null)
            .map((row) => ({
              campus: row.campus,
              location_id: row.locationId,
            })),
        },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["campusMappings", { orgId }]);
        queryClient.invalidateQueries(["reaggregationLocations"]);
        setMappingData([]);
        toast.success("Location mapping saved successfully", {
          autoClose: 3000,
        });
        setFormMode(FORM_MODES.VIEW);
      },
      onError: (error) => {
        toast.error(() => formatUiErrors(error), {
          autoClose: 5000,
          className: "toast-center",
        });
      },
    }
  );

  if (error) return <Error>{formatUiErrors(error)}</Error>;

  const campusData = resolvedMappingData.filter((row) => row.campus !== null);

  const hasCampusData = campusData.length > 0;

  return (
    <>
      {hasCampusData && (
        <div className="flex justify-end gap-4 my-4">
          {formMode === FORM_MODES.VIEW && (
            <Button onClick={() => setFormMode(FORM_MODES.EDIT)}>Edit</Button>
          )}
          {formMode !== FORM_MODES.VIEW && (
            <Button
              emphasis="transparent"
              onClick={() => {
                setFormMode(FORM_MODES.VIEW);
                setMappingData(queryData?.data?.mappings ?? []);
              }}
            >
              Cancel
            </Button>
          )}
        </div>
      )}

      <h1>Location Mapping</h1>

      <div>
        <div className="grid lg:grid-cols-2 w-full items-center">
          <span className="px-4 py-2 text-left font-normal text-table bg-table-header-gray">
            Campus
          </span>
          <span className="px-4 py-2 text-left font-normal text-table bg-table-header-gray">
            SAC Location
          </span>
        </div>

        <div>
          {isFetching && campusData.length === 0 ? (
            <div className="flex w-full justify-center">
              <span className="px-4 py-4 italic text-table">
                Loading data&hellip;
              </span>
            </div>
          ) : (
            campusData.length === 0 && (
              <div className="flex w-full justify-center">
                <span className="px-4 py-4 italic text-table">
                  No campus data found
                </span>
              </div>
            )
          )}
          {campusData.map((row, idx) => (
            <LocationMappingRow
              key={`${idx}_campus`}
              data={row}
              locations={locations}
              idx={idx}
              formMode={formMode}
              handleSave={handleSave}
            />
          ))}
        </div>
      </div>

      {hasCampusData && (
        <div className="flex justify-end gap-4 my-4">
          {formMode !== FORM_MODES.VIEW && (
            <>
              <Button
                disabled={isLoading}
                isLoading={isLoading}
                loadingText={<>Saving&hellip;</>}
                onClick={saveMappings}
                type="button"
              >
                Save
              </Button>
              <Button
                emphasis="transparent"
                onClick={() => {
                  setFormMode(FORM_MODES.VIEW);
                  setMappingData(queryData?.data?.mappings ?? []);
                }}
              >
                Cancel
              </Button>
            </>
          )}
          {formMode === FORM_MODES.VIEW && (
            <Button onClick={() => setFormMode(FORM_MODES.EDIT)}>Edit</Button>
          )}
        </div>
      )}
    </>
  );
}

const LocationMappingRow = ({ data, locations, idx, formMode, handleSave }) => {
  const selectedLocation = locations.find(
    (l) => String(l.id) === String(data.locationId)
  );

  return (
    <Formik
      initialValues={{
        campus: data.campus,
        location_id: data.locationId ? String(data.locationId) : "",
      }}
      enableReinitialize
    >
      {({ values }) => (
        <Form
          className={`${
            idx % 2 === 0 ? "" : "bg-gray-100"
          } pr-4 py-2 border-b grid lg:grid-cols-2 w-full items-center`}
        >
          <FormField
            name="campus"
            label=""
            readOnly
            disabled
            className="mx-4 py-2 bg-transparent"
          />
          {formMode === FORM_MODES.VIEW ? (
            <span className="mx-4 py-2">{selectedLocation?.name ?? "--"}</span>
          ) : (
            <FormField
              name="location_id"
              label=""
              as="select"
              onChange={(e) => {
                handleSave({ ...values, locationId: Number(e.target.value) });
              }}
            >
              <option value="">-- Choose --</option>
              {locations.map((location) => (
                <option value={String(location.id)} key={location.id}>
                  {location.name}
                </option>
              ))}
            </FormField>
          )}
        </Form>
      )}
    </Formik>
  );
};
